/*
    PaymentForm.jsx
    Stripe Payment Form Component for NPO Donations
    サンタナム リシ
*/

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    useStripe, 
    useElements, 
    PaymentElement,
    AddressElement 
} from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FaCreditCard, 
    FaSpinner, 
    FaCheckCircle, 
    FaExclamationTriangle,
    FaArrowLeft,
    FaCalendarAlt,
    FaHeart
} from 'react-icons/fa';

const PaymentForm = ({ 
    donationData, 
    onSuccess, 
    onError, 
    onBack,
    clientSecret,
    isRecurring = false 
}) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    // Determine payment type from client secret format
    const paymentType = clientSecret?.startsWith('seti_') ? 'recurring' : 'one-time';

    // Don't render if we don't have the required clientSecret
    if (!clientSecret) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <FaSpinner className="fa-spin" />
                <p>{t('payment.paymentForm.preparing')}</p>
            </div>
        );
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            setError(t('payment.paymentForm.systemNotReady'));
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            if (paymentType === 'recurring') {
                await handleRecurringPayment();
            } else {
                await handleOneTimePayment();
            }
        } catch (err) {
            setError(err.message || t('payment.paymentForm.processingError'));
            setIsLoading(false);
            if (onError) onError(err);
        }
    };

    const handleOneTimePayment = async () => {
        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/donation-success`,
                receipt_email: donationData.donorInfo.email,
            },
            redirect: 'if_required'
        });

        if (confirmError) {
            setIsLoading(false);
            setError(confirmError.message || t('payment.paymentForm.paymentFailed'));
            if (onError) onError(confirmError);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            // Send confirmation emails
            try {
                await fetch(`${process.env.REACT_APP_API_URL || ''}/api/payment/confirm-and-notify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        paymentIntentId: paymentIntent.id,
                        donorInfo: donationData.donorInfo,
                        amount: donationData.amount,
                        donationCategory: donationData.donationCategory
                    })
                });
            } catch (emailErr) {
                console.error('Failed to send confirmation emails:', emailErr);
            }

            setIsLoading(false);
            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess(paymentIntent);
            }, 1500);
        }
    };

    const handleRecurringPayment = async () => {
        // For recurring payments, we need to save the payment method first
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/subscription-success`,
            },
            redirect: 'if_required'
        });

        if (setupError) {
            setError(setupError.message || t('payment.paymentForm.recurringSetupFailed'));
            setIsLoading(false);
            if (onError) onError(setupError);
            return;
        }

        // Create subscription on backend
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/payment/create-subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...donationData,
                    paymentMethodId: setupIntent.payment_method
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || t('payment.paymentForm.subscriptionFailed'));
            }

            setSuccess(true);
            setIsLoading(false);
            
            setTimeout(() => {
                if (onSuccess) onSuccess(result);
            }, 1500);

        } catch (err) {
            setError(err.message);
            setIsLoading(false);
            if (onError) onError(err);
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: 'JPY'
        }).format(amount);
    };

    if (success) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="payment-success"
            >
                <div className="success-icon">
                    <FaCheckCircle />
                </div>
                <h3>
                    {paymentType === 'recurring' ? t('payment.paymentForm.recurringSuccess') : t('payment.paymentForm.oneTimeSuccess')}
                </h3>
                <p>{t('payment.paymentForm.confirmationEmail')}</p>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="payment-form-container"
        >
            {/* Donation Summary */}
            <div className="donation-summary-card">
                <h4>{t('payment.paymentForm.summaryTitle')}</h4>
                <div className="summary-details">
                    <div className="summary-row">
                        <span>{t('payment.paymentForm.category')}</span>
                        <span>{donationData.donationCategory}</span>
                    </div>
                    <div className="summary-row">
                        <span>{t('payment.paymentForm.amount')}</span>
                        <span className="amount">{formatAmount(donationData.amount)}</span>
                    </div>
                    <div className="summary-row">
                        <span>{t('payment.paymentForm.paymentMethod')}</span>
                        <span>{paymentType === 'recurring' ? t('payment.paymentForm.recurring') : t('payment.paymentForm.oneTime')}</span>
                    </div>
                    <div className="summary-row">
                        <span>{t('payment.paymentForm.donor')}</span>
                        <span>{donationData.donorInfo.name}{t('payment.paymentForm.honorific')}</span>
                    </div>
                </div>
            </div>

            {/* Payment Form */}
            <form onSubmit={handleSubmit} className="payment-form">
                <div className="payment-element-container">
                    <PaymentElement 
                        options={{
                            layout: 'tabs',
                            defaultValues: {
                                billingDetails: {
                                    name: donationData.donorInfo.name,
                                    email: donationData.donorInfo.email
                                }
                            }
                        }}
                    />
                </div>

                {/* Address Element for card payments */}
                <div className="address-element-container">
                    <AddressElement 
                        options={{
                            mode: 'billing',
                            defaultValues: {
                                name: donationData.donorInfo.name,
                            }
                        }}
                    />
                </div>

                {/* Error Display */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="payment-error"
                        >
                            <FaExclamationTriangle />
                            <span>{error}</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Payment Disclaimer */}
                <div className="payment-disclaimer">
                    <p>
                        <strong>{t('payment.paymentForm.disclaimer.title')}</strong><br />
                        • {t('payment.paymentForm.disclaimer.security')}<br />
                        • {paymentType === 'recurring' ? t('payment.paymentForm.disclaimer.recurringNote') : t('payment.paymentForm.disclaimer.oneTimeNote')}<br />
                        • {t('payment.paymentForm.disclaimer.confirmation')}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="payment-actions">
                    <button
                        type="button"
                        className="back-button"
                        onClick={onBack}
                        disabled={isLoading}
                    >
                        <FaArrowLeft />
                        {t('payment.backButton')}
                    </button>
                    
                    <button
                        type="submit"
                        className="submit-payment-button"
                        disabled={!stripe || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <FaSpinner className="spinning" />
                                {paymentType === 'recurring' ? t('payment.paymentForm.recurringProcessing') : t('payment.paymentForm.processing')}
                            </>
                        ) : (
                            <>
                                <FaCreditCard />
                                {paymentType === 'recurring' 
                                    ? t('payment.paymentForm.recurringSubmit', { amount: formatAmount(donationData.amount) })
                                    : t('payment.paymentForm.submit', { amount: formatAmount(donationData.amount) })
                                }
                            </>
                        )}
                    </button>
                </div>
            </form>
        </motion.div>
    );
};

export default PaymentForm;
